import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";

/**
 * components/PersonCollisionModal.js
 * The "two people would look identical" guard's own UI (Person/Role restructure, Stage 3
 * batch 3a, Part B). `utils/persons.js`'s `detectPersonCollision` only DETECTS — it never
 * decides anything on the app's behalf. This modal is what actually asks the human, every
 * time it fires, from all three call sites (`SalespersonFormModal`'s add-person/add-role
 * paths, `KelolaSalesModal`'s rename flow).
 *
 * Governing principle: explain, then offer a real way through — never just block. Two
 * meaningfully different resolutions exist, plus Batal:
 *   - "Pakai yang sudah ada": abandon the person currently being created/modified and reuse
 *     the EXISTING colliding person instead. Only offered when `onUseExisting` is supplied —
 *     see the `canUseExisting` note below for why a rename never gets this button.
 *   - "Lanjutkan, ini orang berbeda": confirms these are genuinely two different people who
 *     will look the same. Transitions to a SECOND internal step, right here, that collects a
 *     required distinguishing note before calling `onContinue(note)` — "the third option must
 *     actually work" means the note is set in this same flow, never a redirect elsewhere.
 *
 * `canUseExisting` (default `true`): a RENAME has no coherent "use the existing person
 * instead" action — that would mean reassigning every one of the renamed person's own roles
 * onto a DIFFERENT person's id, which is a real merge operation this app never performs (see
 * `detectPersonCollision`'s own header comment: names are shown so a human can choose, never
 * used to conclude two records are the same person). `KelolaSalesModal`'s rename flow passes
 * `canUseExisting={false}` and no `onUseExisting` — this dialog then offers exactly two ways
 * through (continue-with-note, or Batal), not three. Add-person and add-role, by contrast,
 * both propose attaching ONE role to ONE person — "use the existing person instead" there just
 * means "attach this role to them instead," a plain, safe, non-merging action.
 *
 * Props:
 *   result          — a `{ambiguous: true, ...}` object from detectPersonCollision.
 *   message         — caller-built explanation text (a node), same "caller supplies the
 *                     specific wording" convention `DeleteConfirmModal.js`'s own `message`
 *                     prop already established — this component never constructs its own
 *                     sentence from `result`'s raw fields, since the right phrasing differs by
 *                     which of the three flows is calling (see each caller's own comment).
 *   canUseExisting  — see above.
 *   initialNote     — pre-fills the note step (e.g. the colliding EXISTING person's own
 *                     current note, so confirming doesn't blank out something already there).
 *   onUseExisting() — called when "Pakai yang sudah ada" is chosen.
 *   onContinue(note)— called once a non-blank note is confirmed on the second step.
 *   onCancel()      — Batal, from either step.
 */
export default function PersonCollisionModal({
  result, message, canUseExisting = true, initialNote = "",
  onUseExisting, onContinue, onCancel,
}) {
  const [step, setStep] = useState("explain"); // "explain" | "note"
  const [note, setNote] = useState(initialNote);
  const noteTrimmed = note.trim();

  useModalEsc(onCancel);

  if (step === "note") {
    return (
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onCancel)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Catatan pembeda</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
            Tambahkan catatan singkat supaya <b>{result.name}</b> yang ini bisa dibedakan dari{" "}
            <b>{result.existing.name}</b> yang sudah ada — misalnya "tua"/"muda", atau ciri lain
            yang biasa dipakai untuk membedakan mereka.
          </div>
          <div className="form-row">
            <label className="form-label" htmlFor="person-collision-catatan">Catatan</label>
            <textarea id="person-collision-catatan" className="form-input" value={note} autoFocus rows={2}
              onChange={(e) => setNote(e.target.value)} />
            {!noteTrimmed && <div className="form-hint" style={{ color: "var(--red)" }}>Wajib diisi.</div>}
          </div>
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep("explain")}>Kembali</button>
            <button type="button" className="btn btn-primary" disabled={!noteTrimmed}
              onClick={() => onContinue(noteTrimmed)}>Lanjutkan</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onCancel)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Dua orang akan terlihat sama</h2>
        <div className="form-hint" style={{ background: "#fef3c7", color: "#92400e", borderRadius: 8, padding: "10px 12px", marginBottom: "var(--gap-lg)" }}>
          {message}
        </div>
        <div className="modal-actions" style={{ flexDirection: "column", alignItems: "stretch", gap: "var(--gap-sm)" }}>
          {canUseExisting && (
            <button type="button" className="btn btn-block" onClick={onUseExisting}>
              Apakah ini {result.existing.name} yang sama? — Pakai yang sudah ada
            </button>
          )}
          <button type="button" className="btn btn-block" onClick={() => setStep("note")}>
            Memang dua orang berbeda? — Lanjutkan, beri catatan pembeda
          </button>
          <button type="button" className="btn btn-block" onClick={onCancel}>Batal</button>
        </div>
      </div>
    </div>
  );
}
